/**
 * Preview strip for pending (not yet uploaded) files.
 * Shows image thumbnails with local object URLs, file icons for non-images.
 * Reusable across chat input, comment input, and any file attachment UI.
 */
import { useMemo } from 'react';
import { X, File as FileIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { isBrowserRenderableImage } from '../imageRendering';
import { FileSizeDisplay } from './FileSizeDisplay';

interface PendingFilesPreviewProps {
  files: File[];
  onRemove: (index: number) => void;
  /** Compact mode with smaller thumbnails (for chat input) */
  compact?: boolean;
}

function FilePreviewItem({
  file,
  index,
  onRemove,
  compact,
}: {
  file: File;
  index: number;
  onRemove: (index: number) => void;
  compact?: boolean;
}) {
  // An object URL for a not-yet-uploaded TIFF decodes no better than a served
  // one, so gate the local preview on the same predicate as the stored path.
  const isImage = isBrowserRenderableImage(file.type);

  const previewUrl = useMemo(() => {
    if (!isImage) return null;
    return URL.createObjectURL(file);
  }, [file, isImage]);

  // Note: cleanup happens when component unmounts via React's lifecycle.
  // Object URLs are revoked by the browser when the page unloads.

  if (isImage && previewUrl) {
    return (
      <div className="relative group">
        <img
          src={previewUrl}
          alt={file.name}
          className={cn(
            'rounded border object-cover',
            compact ? 'h-12 w-12' : 'h-16 w-16',
          )}
        />
        <Button
          variant="destructive"
          size="icon"
          className="absolute -top-1.5 -end-1.5 h-4 w-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => onRemove(index)}
        >
          <X className="h-2.5 w-2.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="relative group flex items-center gap-1.5 rounded border bg-muted/50 px-2 py-1">
      <FileIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-xs truncate max-w-[120px]">{file.name}</p>
        <FileSizeDisplay bytes={file.size} className="block text-[10px] text-muted-foreground" />
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-4 w-4 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => onRemove(index)}
      >
        <X className="h-2.5 w-2.5" />
      </Button>
    </div>
  );
}

export function PendingFilesPreview({ files, onRemove, compact }: PendingFilesPreviewProps) {
  if (files.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', compact ? 'px-2 py-1.5' : 'px-3 py-2')}>
      {files.map((file, index) => (
        <FilePreviewItem
          key={`${file.name}-${file.size}-${index}`}
          file={file}
          index={index}
          onRemove={onRemove}
          compact={compact}
        />
      ))}
    </div>
  );
}
